/**
 * PATH: packages/drafting/src/export/exporters.ts
 * FILE_ID: DRAFTING-EXPORT-EXPORTERS-001
 * PURPOSE: Turn the IR (or the live Konva stage) into downloadable
 *   artifacts — PNG (raster), SVG (clean vector, IR-derived), JSON (the
 *   IR itself). SVG/JSON are renderer-independent on purpose: they prove
 *   the IR is the source of truth, not the canvas.
 */
import type Konva from 'konva'
import type { DrawingIR, Entity, Layer, Point } from '../ir/schema'
import { paperSizeMm } from '../canvas/geometry'

const DEFAULT_STROKE = '#2c3a47'

function strokeOf(e: Entity, layer?: Layer): string {
  return e.stroke ?? layer?.stroke ?? DEFAULT_STROKE
}
function fillOf(e: Entity, layer?: Layer): string {
  const raw = e.fill ?? layer?.color
  if (!raw || raw === 'none' || raw.startsWith('hatch:')) return 'none'
  return raw
}
function escapeXml(s: string): string {
  return s.replace(/[<>&"']/g, (c) => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;', "'": '&apos;' }[c]!))
}
const pts = (p: Point[]) => p.map(([x, y]) => `${x},${y}`).join(' ')

function entityToSvg(e: Entity, layer?: Layer): string {
  const s = strokeOf(e, layer)
  const sw = e.strokeWidth ?? 1
  const dash = e.dash ? ` stroke-dasharray="${e.dash.join(',')}"` : ''
  const f = fillOf(e, layer)
  switch (e.type) {
    case 'line':
      return `<line x1="${e.a[0]}" y1="${e.a[1]}" x2="${e.b[0]}" y2="${e.b[1]}" stroke="${s}" stroke-width="${sw}"${dash}/>`
    case 'polyline':
      return `<${e.closed ? 'polygon' : 'polyline'} points="${pts(e.points)}" fill="${e.closed ? f : 'none'}" stroke="${s}" stroke-width="${sw}"${dash}/>`
    case 'hatch':
      return `<polygon points="${pts(e.points)}" fill="${f === 'none' ? 'rgba(44,58,71,0.12)' : f}" stroke="${s}" stroke-width="${sw}"/>`
    case 'rect':
      return `<rect x="${e.x}" y="${e.y}" width="${e.w}" height="${e.h}" fill="${f}" stroke="${s}" stroke-width="${sw}"${dash}/>`
    case 'circle':
    case 'arc':
      return `<circle cx="${e.cx}" cy="${e.cy}" r="${e.r}" fill="${e.type === 'circle' ? f : 'none'}" stroke="${s}" stroke-width="${sw}"/>`
    case 'text':
      return `<text x="${e.at[0]}" y="${e.at[1]}" font-size="${e.size ?? 12}" fill="${s}" transform="rotate(${e.rotation ?? 0} ${e.at[0]} ${e.at[1]})">${escapeXml(e.text)}</text>`
    case 'dimension': {
      const label = e.value != null ? `${e.value}${e.unit ?? ''}` : ''
      const mx = (e.a[0] + e.b[0]) / 2
      const my = (e.a[1] + e.b[1]) / 2 - (e.offset ?? 8)
      return `<line x1="${e.a[0]}" y1="${e.a[1]}" x2="${e.b[0]}" y2="${e.b[1]}" stroke="${s}" stroke-width="${sw}" marker-start="url(#arw)" marker-end="url(#arw)"/>${label ? `<text x="${mx}" y="${my}" font-size="11" fill="${s}">${escapeXml(label)}</text>` : ''}`
    }
    case 'leader':
      return `<line x1="${e.from[0]}" y1="${e.from[1]}" x2="${e.to[0]}" y2="${e.to[1]}" stroke="${s}" stroke-width="${sw}" marker-end="url(#arw)"/><text x="${e.to[0] + 4}" y="${e.to[1] - 6}" font-size="11" fill="${s}">${escapeXml(e.text)}</text>`
    case 'group':
      return ''
  }
}

/** drawingToSvg — pure IR -> SVG string (viewBox = paper in drawing units). */
export function drawingToSvg(ir: DrawingIR): string {
  const paper = paperSizeMm(ir.meta.paper)
  const layerById = new Map(ir.layers.map((l) => [l.id, l]))
  const body = ir.entities
    .filter((e) => layerById.get(e.layer)?.visible !== false)
    .map((e) => entityToSvg(e, layerById.get(e.layer)))
    .join('\n  ')
  return [
    `<svg xmlns="http://www.w3.org/2000/svg" width="${paper.w}mm" height="${paper.h}mm" viewBox="0 0 ${paper.w} ${paper.h}">`,
    `  <defs><marker id="arw" markerWidth="8" markerHeight="8" refX="4" refY="4" orient="auto"><path d="M0,0 L8,4 L0,8 z" fill="${DEFAULT_STROKE}"/></marker></defs>`,
    `  <rect x="0" y="0" width="${paper.w}" height="${paper.h}" fill="#ffffff"/>`,
    `  ${body}`,
    `</svg>`,
  ].join('\n')
}

function triggerDownload(href: string, filename: string): void {
  const a = document.createElement('a')
  a.href = href
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
}

const slug = (s: string) => (s || 'drawing').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')

export function downloadJson(ir: DrawingIR): void {
  const blob = new Blob([JSON.stringify(ir, null, 2)], { type: 'application/json' })
  const url = URL.createObjectURL(blob)
  triggerDownload(url, `${slug(ir.meta.title)}.json`)
  URL.revokeObjectURL(url)
}

export function downloadSvg(ir: DrawingIR): void {
  const blob = new Blob([drawingToSvg(ir)], { type: 'image/svg+xml' })
  const url = URL.createObjectURL(blob)
  triggerDownload(url, `${slug(ir.meta.title)}.svg`)
  URL.revokeObjectURL(url)
}

/** downloadPng — raster the live Konva stage at 2× for crisp output. */
export function downloadPng(stage: Konva.Stage | null, ir: DrawingIR): void {
  const url = stagePngDataUrl(stage)
  if (!url) return
  triggerDownload(url, drawingFilename(ir, 'png'))
}

/** drawingFilename — slugged "<title>.<ext>" used by exports + email handoff. */
export function drawingFilename(ir: DrawingIR, ext: string): string {
  return `${slug(ir.meta.title)}.${ext}`
}

/** stagePngDataUrl — the live stage as a 2× PNG data URL (null if no stage).
 *  Shared by PNG download, inline-email insert, and attachment build so the
 *  three paths can never raster differently. */
export function stagePngDataUrl(stage: Konva.Stage | null): string | null {
  if (!stage) return null
  return stage.toDataURL({ pixelRatio: 2 })
}

/** dataUrlToFile — decode a `data:<mime>;base64,...` URL into a File so the
 *  drawing can be handed to the email compose attachment list. Pure I→O. */
export function dataUrlToFile(dataUrl: string, filename: string): File {
  const comma = dataUrl.indexOf(',')
  const header = dataUrl.slice(0, comma)
  const mime = /data:([^;]+)/.exec(header)?.[1] ?? 'application/octet-stream'
  const binary = atob(dataUrl.slice(comma + 1))
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i)
  return new File([bytes], filename, { type: mime })
}

/** svgToFile — the IR-derived SVG as a File (clean vector attachment). */
export function svgToFile(ir: DrawingIR): File {
  return new File([drawingToSvg(ir)], drawingFilename(ir, 'svg'), {
    type: 'image/svg+xml',
  })
}
